import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Spin, Tooltip } from 'antd';
import moment from 'moment';

import AccountNav from '../../components/AccountNav/AccountNav';
import AccountNavMobile from '../../components/AccountNavMobile/AccountNavMobile';
import ListingAgreement from '../../components/ListingAgreement/ListingAgreement';
import MarketFooter from '../../components/MarketFooter/MarketFooter';
import PinDrawerWithoutButton from '../../components/PinDrawerWithoutButton/PinDrawerWithoutButton';
import PinModalWithoutButton from '../../components/PinModalWithoutButton/PinModalWithoutButton';
import PortfolioCard from '../../components/PortfolioCard/PortfolioCard';
import ResponsiveSurface from '../../components/ResponsiveSurface/ResponsiveSurface';
import EditOrderDrawer from './EditOrderDrawer';
import EditOrderModal from './EditOrderModal';
import PortfolioVerificationBanner from './PortfolioVerificationBanner';
import PortfolioOverviewSection from './PortfolioOverviewSection';
import PortfolioHoldingsSection from './PortfolioHoldingsSection';

import { Information, NextArrow } from '../../assets/icons';

import useGetUserProfile from '../../Hooks/useGetUserProfile';
import useMediaQuery from '../../Hooks/useMediaQuery';
import useQuickBuySecondary from '../../Hooks/useQuickBuySecondary';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';
import { fetchOpenOrderData, fetchPortfolioChart } from '../../services/PortfolioApi/portfolioApi';
import { fetchSecondaryOfferingListAction } from '../../store/reducer/secondaryOfferingSlice';
import { fetchFundingSourcesAction } from '../../store/reducer/fundingSourcesSlice';
import { getDateFormat } from '../../utils';
import { DATE_FORMAT, formatDate } from '../../utils/dateFormat.helper';
import { toastNotify } from '../../utils/toastNotify';
import { getUsCurrencyFormat, getUsCurrencyFormatWithoutDecimal, getUsPercentageFormat } from '../../utils/usCurrencyFormatter';

import './Portfolio.scss';

const TIMELINE_LABELS = ['7D', '1M', '6M', '1Y', 'YTD', 'MAX'];

const INTERVALS = {
  '7D': 0,
  '1M': 6,
  '6M': 0,
  '1Y': 0,
  YTD: 0,
  MAX: 0,
};
const Portfolio = (props) => {
  const dispatch = useDispatch();
  const screenMD = useMediaQuery('(min-width: 1000px)');
  const [openQuickBuy, setOpenQuickBuy] = useState();
  const [quickBuyPropertyData, setQuickBuyPropertyData] = useState({});
  const [isShowFullDate, setIsShowFullDate] = useState(true);
  const { data: propertyDetails, loading: propertyLoading } = useSelector((state) => state.propertyDetails);
  const quickBuySecondaryOptions = useQuickBuySecondary(setOpenQuickBuy, quickBuyPropertyData, {}, { skipEligibilityToast: true });
  const { isEntityRestricted } = useSecondaryMarketEligibility();
  const orderRef = useRef();
  const userData = useSelector((state) => state.userData);
  const secondaryMarketPropertyList = useSelector((state) => state.secondaryOffering.sliderData);
  const [openOrdertableData, setOpenOrderTableData] = useState({});

  // Check if secondary market is paused
  const isSecondaryMarketPaused = useMemo(() => {
    return secondaryMarketPropertyList?.data?.isSecondaryMarketPaused === true;
  }, [secondaryMarketPropertyList?.data?.isSecondaryMarketPaused]);

  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const [isOpenOrderLoading, setIsOpenOrderLoading] = useState(false);
  const [isPortfolioDataLoading, setIsPortfolioDataLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const SEONDARYOBJ = {
    propertyId: 0,
    propertyName: '',
    label: '',
    cityName: '',
    cityId: 0,
    stateId: 0,
    stateName: '',
    propertyCreatedAt: '',
    images: {},
    propertyType: '',
    dividendYield: null,
    lastTradedPrice: 0,
    allTime: 0.0,
    bids: 0,
    asks: 0,
    likedByLoggedInUser: null,
  };
  const assetTableColumns = [
    {
      title: 'Property',
      width: 100,
      dataIndex: 'propertyName',
      key: 'name',
      fixed: 'left',
      render: (val, record) => {
        return (
          <div className='title'>
            {val}
            <Link to={`/portfolio/assets/${record?.propertyId}`}>
              {' '}
              <NextArrow className='cursor-pointer' color='#1A1519' size={10} />
            </Link>
          </div>
        );
      },
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Total value
          <Tooltip
            title='Total Value is calculated by multiplying your total shares by the most recent executed trade price on the secondary marketplace for that property. If no secondary trades have occurred, the value defaults to $10 per share. This is an estimated market reference value and does not represent a guaranteed sale price or fair market value.'
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              <Information size={14} color='#958b88' verticalAlign='-2px' />
            </span>
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalValue',
      key: '1',
      width: 50,
      render: (val) => `$${getUsCurrencyFormat(Number(val) || 0)}`,
    },
    {
      title: 'Total gain',
      dataIndex: 'totalGain',
      key: '2',
      width: 50,
      render: (val) =>
        Number(val) === 0 ? (
          <span>$0</span>
        ) : (
          <span className={Number(val) > 0 ? 'text-success' : 'text-error'}>
            {Number(val) > 0 ? '+' : '-'} ${getUsCurrencyFormat(Number(val) > 0 ? Number(val) : -Number(val) || 0)}
          </span>
        ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: '3',
      width: 50,
      render: (val) => `${getUsCurrencyFormatWithoutDecimal(Number(val))}`,
    },
    {
      title: 'Avg Price Paid',
      dataIndex: 'averagePrice',
      key: '5',
      width: 50,
      render: (val) => `$${getUsCurrencyFormat(Number(val || 0))}`,
    },
    {
      title: 'Best bid',
      dataIndex: 'highestBid',
      key: '4',
      width: 50,
      render: (val) => `$${getUsCurrencyFormat(Number(val) || 0)}`,
    },
    {
      title: 'Portfolio Volume',
      dataIndex: 'portfolioVolume',
      key: '4',
      width: 50,
      render: (val) => `${getUsCurrencyFormat(Number(val) || 0)} %`,
    },
    {
      title: ' ',
      key: 'operation',
      width: 50,
      render: (val) => {
        const isSellOrdersTradable = val?.isSellOrdersTradable;
        const isDisabled = isSecondaryMarketPaused || !isSellOrdersTradable;
        const tooltipTitle = isSecondaryMarketPaused
          ? 'The Realbricks secondary market trading is temporarily paused.'
          : !isSellOrdersTradable
          ? 'All shares are listed for sale in open orders.'
          : '';
        return (
          <Tooltip title={tooltipTitle}>
            <span
              className={`fw-600 ${isDisabled ? 'text-taupe-60 cursor-not-allowed' : 'text-copper cursor-pointer'}`}
              onClick={() => {
                if (!isDisabled) {
                  setQuickBuyPropertyData(val);
                  quickBuySecondaryOptions.openForBuy();
                }
              }}
              style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              Trade
            </span>
          </Tooltip>
        );
      },
    },
  ];
  const orderTableColumns = [
    {
      title: 'Property',
      width: 100,
      dataIndex: 'property',
      key: 'name',
      fixed: 'left',
    },
    {
      title: 'Category',
      width: 100,
      dataIndex: 'isBonusShare',
      key: 'isBonusShare',
      // fixed: 'left',
      render: (val) => (val ? 'Awarded' : 'Purchased'),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 50,
      render: (val) => formatDate(val, DATE_FORMAT.DATE_FORMAT_SHORT),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: '1',
      width: 50,
    },
    {
      title: 'Total purchase',
      dataIndex: 'totalPrice',
      key: '2',
      width: 50,
      render: (val) => `$${getUsCurrencyFormat(val)}`,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: '3',
      width: 50,
      render: (val) => `${getUsCurrencyFormatWithoutDecimal(val)}`,
    },
    {
      title: 'Order price',
      dataIndex: 'orderPrice',
      key: '4',
      width: 50,
      render: (val) => `$${getUsCurrencyFormat(val)}`,
    },
    {
      title: 'Last price',
      dataIndex: 'lastPrice',
      key: '5',
      width: 50,
      render: (val) => `$${getUsCurrencyFormat(val)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: '6',
      width: 50,
    },
    {
      title: ' ',
      key: 'operation',
      width: 50,
      render: (val) => {
        return (
          <>
            {val?.type === 'IPO' ? (
              <span
                className='fw-600 text-copper cursor-pointer'
                onClick={() => {
                  quickBuySecondaryOptions?.handleFeatchOpenOrderDetail(val);
                }}
              >
                View
              </span>
            ) : (
              <span
                className='fw-600 text-copper cursor-pointer'
                onClick={() => {
                  quickBuySecondaryOptions?.setStoreOrderValue(val);
                  quickBuySecondaryOptions?.setOpenPinModal(true);
                }}
              >
                Edit
              </span>
            )}
          </>
        );
      },
    },
  ];

  const openOrdersRef = useRef(null);
  const location = useLocation();

  const handleOnScrollIntoView = () => {
    openOrdersRef?.current?.scrollIntoView();
  };
  useEffect(() => {
    if (location.hash === '#open-orders') {
      handleOnScrollIntoView();
    }
  }, [location]);

  const screenSm = useMediaQuery('(max-width: 639px)');
  const [loading, setLoading] = useState(true);
  const [tableLoader, setTableLoader] = useState(false);
  const [selectedTimeline, setSelectedTimeline] = useState('1M');
  const [isFetchingChart, setIsFetchingChart] = useState(false);
  const [timelineChartData, setTimelineChartData] = useState(null);
  const [timelineLabels, setTimelineLabels] = useState(TIMELINE_LABELS);
  const [assetsData, setAssetsData] = useState([]);
  const [portfolioAge, setPortfolioAge] = useState(0);
  const [portfolioStartDate, setPortfolioStartDate] = useState('');
  const cardRef = useRef(null);

  useGetUserProfile();

  const handleSelectTimeline = useCallback(async (label) => {
    if (isChartDataLoading) return; // Prevent duplicate calls
    if (selectedTimeline === label && timelineChartData) return; // Skip if already loaded
    try {
      setSelectedTimeline(label);
      setIsChartDataLoading(true);
      setIsFetchingChart(true);
      const data = await fetchPortfolioChart(label);
      setTimelineChartData(data?.data);
    } catch (err) {
      toastNotify('error', err?.response?.data?.message || 'Failed getting chart data');
    } finally {
      setIsChartDataLoading(false);
      setIsFetchingChart(false);
    }
  }, [isChartDataLoading, selectedTimeline, timelineChartData]);

  const fetchOpenOrderTableData = useCallback(async () => {
    if (isOpenOrderLoading) return; // Prevent multiple calls

    setIsOpenOrderLoading(true);
    setTableLoader(true);
    try {
      const openOrderResp = await fetchOpenOrderData({ page: 1, perPage: 200 });
      const openOrderResponse = openOrderResp.data;
      let openOrder = {
        ...openOrderResponse,
        openOrders: openOrderResponse?.openOrders,
      };
      if (!openOrderResp.isError) {
        setOpenOrderTableData({ ...openOrder });
      } else {
        toastNotify('error', openOrderResp?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setIsOpenOrderLoading(false);
      setTableLoader(false);
    }
  }, [isOpenOrderLoading]);

  const fetchPortfolioData = useCallback(async () => {
     if (isPortfolioDataLoading) return; // Prevent multiple calls
     setIsPortfolioDataLoading(true);
    const cardsData = await cardRef?.current?.fetchCardData();
    const assetData = cardsData?.myAssets || [];
    setAssetsData(assetData);
    const startDate = cardsData?.startDate;
    const ageInMonths = moment().diff(moment(startDate || new Date()), 'months');
    setPortfolioStartDate(startDate);
    setPortfolioAge(ageInMonths);
    if (ageInMonths < 6) {
      setTimelineLabels((state) => state.filter((x) => x !== '6M' && x !== '1Y'));
    }
    if(!timelineChartData){
      await handleSelectTimeline('1M');
    }
    setIsPortfolioDataLoading(false);
  }, [isPortfolioDataLoading, timelineChartData, handleSelectTimeline]);
  const handleOpenEditDrawer = (value) => {
    if (value?.type === 'IPO') {
      quickBuySecondaryOptions?.handleFeatchOpenOrderDetail(value);
    } else {
      quickBuySecondaryOptions?.setStoreOrderValue(value);
      quickBuySecondaryOptions?.setOpenPinModal(true);
    }
  };
  const generateDummyDataBetweenDates = (start, end = moment(), diff = 'day') => {
    try {
      const data = [];
      for (let i = moment(start); i.isBefore(moment(end)); i.add(1, diff)) {
        data.push({
          date: i.toISOString(),
          total: 0,
          sharesTotal: 0,
          dividendTotal: 0,
          totalInvestmentAmount: 0,
        });
      }
      return data;
    } catch (error) {
      // A malformed date range would otherwise loop indefinitely/throw — fall back to
      // an empty placeholder series so the chart just renders blank instead of crashing.
      return [];
    }
  };

  const generateDummyDataBetweenMonths = (start, end = moment(), diff = 'day') => {
    const startMonth = moment(start).month();
    const endMonth = moment(end).month();
    const durationInDays = moment(end).diff(start, 'days');
    if (durationInDays <= 31) {
      // If the difference between start and end is less than or equal to 31 days, generate day-wise data
      return generateDummyDataBetweenDates(start, end, 'day');
    }
    // Generate month-wise data
    const data = [];
    let tempDate = moment(start);
    try {
      for (let i = startMonth; i <= endMonth; i++) {
        const monthEndDate = tempDate.endOf('month').toDate();
        const lastDayOfMonth = moment(monthEndDate).date();
        for (let j = tempDate.date(); j <= lastDayOfMonth; j++) {
          data.push({
            date: tempDate.toISOString(),
            total: 0,
            sharesTotal: 0,
            dividendTotal: 0,
            totalInvestmentAmount: 0,
          });
          tempDate.add(1, 'day');
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating data:', error);
    }
    return data;
  };
  const navigate = useNavigate();

  // Fetch secondary market data to get latest isSecondaryMarketPaused status.
  // Skipped for Entity accounts — they have no Trade button to gate (RDS-921).
  useEffect(() => {
    if (!isEntityRestricted) {
      dispatch(fetchSecondaryOfferingListAction({ page: 1, perPage: 9 }));
    }
    dispatch(fetchFundingSourcesAction());
  }, [dispatch, isEntityRestricted]);

  // Fix the useEffect to prevent multiple calls
  useEffect(() => {
    const initializeData = async () => {
      if (!isInitialized) {
        setIsInitialized(true);
        await Promise.all([fetchPortfolioData(), fetchOpenOrderTableData()]);
        setLoading(false);
      }
    };
    initializeData();
  }, [isInitialized, fetchPortfolioData, fetchOpenOrderTableData]);

  const chartData = useMemo(() => {
    if (isFetchingChart || !timelineChartData) return [];
    let fullDateOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    switch (selectedTimeline) {
      case '7D':
        setIsShowFullDate(true);
        return (
          generateDummyDataBetweenDates(timelineChartData?.startDate, timelineChartData?.endDate, 'days')?.map((x) => {
            const currentDateDataFromAPI = timelineChartData?.chartData?.find((apiData) => moment(apiData.date).isSame(x.date, 'day'));
            if (currentDateDataFromAPI) {
              return {
                ...currentDateDataFromAPI,
                fullDate: getDateFormat({
                  date: new Date(x.date),
                  options: fullDateOptions,
                }),
                date: getDateFormat({
                  date: new Date(x.date),
                  options: {
                    weekday: 'short',
                  },
                }),
              };
            }
            return {
              ...x,
              fullDate: getDateFormat({
                date: new Date(x.date),
                options: fullDateOptions,
              }),
              date: getDateFormat({
                date: new Date(x.date),
                options: {
                  weekday: 'short',
                },
              }),
            };
          }) || []
        );
      case '1M':
        setIsShowFullDate(true);
        return (
          generateDummyDataBetweenDates(timelineChartData?.startDate, timelineChartData?.endDate, 'days')?.map((x) => {
            const currentDateDataFromAPI = timelineChartData?.chartData?.find((apiData) => moment(apiData.date).isSame(x.date, 'day'));
            if (currentDateDataFromAPI) {
              return {
                ...currentDateDataFromAPI,
                fullDate: getDateFormat({
                  date: new Date(x.date),
                  options: fullDateOptions,
                }),
                date: getDateFormat({
                  date: new Date(x.date),
                  options: {
                    month: 'short',
                    day: 'numeric',
                  },
                }),
              };
            }
            return {
              ...x,
              fullDate: getDateFormat({
                date: new Date(x.date),
                options: fullDateOptions,
              }),
              date: getDateFormat({
                date: new Date(x.date),
                options: {
                  month: 'short',
                  day: 'numeric',
                },
              }),
            };
          }) || []
        );
      case '6M':
        setIsShowFullDate(false);
        return (
          generateDummyDataBetweenDates(timelineChartData?.startDate, timelineChartData?.endDate, 'months')?.map((x) => {
            const currentDateDataFromAPI = timelineChartData?.chartData?.find((apiData) => moment(apiData.date).isSame(x.date, 'month'));
            if (currentDateDataFromAPI) {
              return {
                ...currentDateDataFromAPI,
                fullDate: getDateFormat({
                  date: new Date(x.date),
                  options: fullDateOptions,
                }),
                date: getDateFormat({
                  date: new Date(x.date),
                  options: {
                    month: 'short',
                  },
                }),
              };
            }
            return {
              ...x,
              fullDate: getDateFormat({
                date: new Date(x.date),
                options: fullDateOptions,
              }),
              date: getDateFormat({
                date: new Date(x.date),
                options: {
                  month: 'short',
                },
              }),
            };
          }) || []
        );

      case 'YTD':
        setIsShowFullDate(false);
        // let YTDData =
        // generateDummyDataBetweenDates(timelineChartData?.startDate, 'months')
        // YTDData[YTDData.length - 1].date = timelineChartData.chartData[timelineChartData.chartData.length-1]?.date;
        return (
          generateDummyDataBetweenDates(timelineChartData?.startDate, moment(), 'months').map((x) => {
            const currentDateDataFromAPI = timelineChartData?.chartData?.find((apiData) => moment(apiData.date).isSame(x.date, 'month'));
            if (currentDateDataFromAPI) {
              return {
                ...currentDateDataFromAPI,
                fullDate: getDateFormat({
                  date: new Date(x.date),
                  options: fullDateOptions,
                }),
                date: getDateFormat({
                  date: new Date(x.date),
                  options: { month: 'short', year: 'numeric' },
                }),
              };
            }
            return {
              ...x,
              fullDate: getDateFormat({
                date: new Date(x.date),
                options: fullDateOptions,
              }),
              date: getDateFormat({
                date: new Date(x.date),
                options: {
                  month: 'short',
                  year: 'numeric',
                },
              }),
            };
          }) || []
        );
      case '1Y':
        return (
          generateDummyDataBetweenDates(timelineChartData?.startDate, timelineChartData?.endDate, 'months')?.map((x) => {
            const currentDateDataFromAPI = timelineChartData?.chartData?.find((apiData) => moment(apiData.date).isSame(x.date, 'month'));
            if (currentDateDataFromAPI) {
              return {
                ...currentDateDataFromAPI,
                fullDate: getDateFormat({
                  date: new Date(x.date),
                  options: fullDateOptions,
                }),
                date: getDateFormat({
                  date: new Date(x.date),
                  options: {
                    month: 'short',
                  },
                }),
              };
            }
            return {
              ...x,
              fullDate: getDateFormat({
                date: new Date(x.date),
                options: fullDateOptions,
              }),
              date: getDateFormat({
                date: new Date(x.date),
                options: {
                  month: 'short',
                },
              }),
            };
          }) || []
        );
      case 'MAX':
        // This depends on portfolio age
        let options = {
          month: 'short',
          day: 'numeric',
        };
        setIsShowFullDate(true);
        let diff = 'days';
        if (portfolioAge >= 1) {
          setIsShowFullDate(false);
          options = {
            month: 'short',
            year: 'numeric',
          };
          diff = 'months';
        }
        let MAXData = generateDummyDataBetweenMonths(portfolioStartDate, timelineChartData.endDate, diff);

        if (MAXData.length) {
          MAXData[MAXData.length - 1].date = timelineChartData.chartData[timelineChartData.chartData.length - 1]?.date;
        } else {
          MAXData.push({
            date: timelineChartData.endDate,
            total: 0,
            sharesTotal: 0,
            dividendTotal: 0,
            totalInvestmentAmount: 0,
          });
        }

        return (
          MAXData?.map((x) => {
            const currentDateDataFromAPI = timelineChartData?.chartData?.find((apiData) => moment(apiData.date).isSame(x.date, diff));
            if (currentDateDataFromAPI) {
              return {
                ...currentDateDataFromAPI,
                fullDate: getDateFormat({
                  date: new Date(x.date),
                  options: fullDateOptions,
                }),
                date: getDateFormat({
                  date: new Date(x.date),
                  options,
                }),
              };
            }
            return {
              ...x,
              fullDate: getDateFormat({
                date: new Date(x.date),
                options: fullDateOptions,
              }),
              date: getDateFormat({
                date: new Date(x.date),
                options,
              }),
            };
          }) || []
        );

      default:
        break;
    }
  }, [selectedTimeline, isFetchingChart, timelineChartData, portfolioAge]);

  const getPortfolioBarchartInterval = () => {
    if (selectedTimeline === 'MAX') {
      if (portfolioAge < 15) {
        return 0; // Set interval to 0 if the portfolio age is less than 15 days
      } else if (portfolioAge > 1) {
        return parseInt(chartData.length / 6); // Set interval to 1/6th of the data length if the portfolio age is greater than 1 year
      } else {
        return 1; // Set interval to 1 day if the portfolio age is greater than 15 days and less than 1 year
      }
    } else {
      return INTERVALS[selectedTimeline]; // Use the regular interval for other timelines
    }
  };
  const portfolioBarchartInterval = getPortfolioBarchartInterval();
  const hasPortfolioData = Boolean(openOrdertableData?.openOrders?.length || assetsData?.length);

  const qucikBuyPropertyDataObject = useMemo(() => {
    return {
      ...SEONDARYOBJ,
      propertyId: propertyDetails?.data?.propertyId,
      propertyName: propertyDetails?.data?.name,
      label: propertyDetails?.data?.tag,
      cityName: propertyDetails?.data?.city,
      cityId: 0,
      stateId: 0,
      stateName: propertyDetails?.data?.state,
      propertyCreatedAt: '',
      images: propertyDetails?.data?.images,
      propertyType: propertyDetails?.data?.propertyTypeName,
      dividendYield: propertyDetails?.data?.tenantStatus?.dividendYield,
      lastTradedPrice: Number(propertyDetails?.data?.lastPrice),
      allTime: getUsPercentageFormat(
        (100 * (Number(propertyDetails?.data?.lastPrice) - Number(propertyDetails?.data?.finance?.perSharePrice))) /
        Number(propertyDetails?.data?.finance?.perSharePrice) || 0
      ),
      bids: propertyDetails?.data?.totalBids,
      asks: propertyDetails?.data?.totalAsks,
      likedByLoggedInUser: null,
    };
  }, [propertyDetails]);

  return (
    <>
      <div className='portfolio'>
        <div className='sticky-nav'>
          <AccountNav />
          <AccountNavMobile />
        </div>
        <PortfolioVerificationBanner userData={userData} navigate={navigate} />
        <div className='portfolio__head'>
          <div className='container-fluid'>
            <h3>Portfolio</h3>
          </div>
        </div>

        <Spin spinning={loading}>
          <PortfolioCard setLoading={setLoading} ref={cardRef} isInvested={hasPortfolioData} />

          <PortfolioOverviewSection
            hasPortfolioData={hasPortfolioData}
            loading={loading}
            timelineLabels={timelineLabels}
            selectedTimeline={selectedTimeline}
            onSelectTimeline={handleSelectTimeline}
          />
        </Spin>
        <PortfolioHoldingsSection
          hasPortfolioData={hasPortfolioData}
          isFetchingChart={isFetchingChart}
          chartData={chartData}
          portfolioBarchartInterval={portfolioBarchartInterval}
          isShowFullDate={isShowFullDate}
          openOrdersRef={openOrdersRef}
          screenSm={screenSm}
          openOrdertableData={openOrdertableData}
          tableLoader={tableLoader}
          orderTableColumns={orderTableColumns}
          handleOnScrollIntoView={handleOnScrollIntoView}
          handleOpenEditDrawer={handleOpenEditDrawer}
          assetsData={assetsData}
          assetTableColumns={isEntityRestricted ? assetTableColumns.filter((col) => col.key !== 'operation') : assetTableColumns}
          navigate={navigate}
          screenMD={screenMD}
          quickBuySecondaryOptions={quickBuySecondaryOptions}
          qucikBuyPropertyDataObject={qucikBuyPropertyDataObject}
          fetchOpenOrderTableData={fetchOpenOrderTableData}
          cardRef={cardRef}
          openQuickBuy={openQuickBuy}
          setOpenQuickBuy={setOpenQuickBuy}
        />
        <ResponsiveSurface
          isDesktop={screenMD}
          DesktopComponent={EditOrderModal}
          MobileComponent={EditOrderDrawer}
          selectedProperty={quickBuySecondaryOptions?.selectedProperty}
          propertyDetails={propertyDetails}
          properyLoading={propertyLoading}
          isSecondaryMarketPaused={isSecondaryMarketPaused}
          setOpenModal={(value) => {
            fetchOpenOrderTableData();
            quickBuySecondaryOptions?.setOpenModal(value);
          }}
          onOrderActionSuccess={() => cardRef?.current?.fetchCardData(true)}
          desktopProps={{ EditOrderOne: quickBuySecondaryOptions?.openModal, CloseFunction: quickBuySecondaryOptions?.setOpenModal }}
          mobileProps={{ EditOrderOneDrawer: quickBuySecondaryOptions?.openModal, CloseDrawer: quickBuySecondaryOptions?.setOpenModal }}
        />
        {quickBuySecondaryOptions?.openPinModal && (
          <ResponsiveSurface
            isDesktop={screenMD}
            DesktopComponent={PinModalWithoutButton}
            MobileComponent={PinDrawerWithoutButton}
            onCancel={() => {
              quickBuySecondaryOptions?.setOpenPinModal(false);
            }}
            open={quickBuySecondaryOptions?.openPinModal}
            pin={quickBuySecondaryOptions?.pin}
            loading={quickBuySecondaryOptions?.loading}
            handleForgotPin={quickBuySecondaryOptions?.handleForgotPin}
            setPin={quickBuySecondaryOptions?.setPin}
            setLoading={quickBuySecondaryOptions?.setLoading}
            onSuccess={(isTOSAccepted) => {
              orderRef?.current?.resetFields();
              quickBuySecondaryOptions?.handleVerifyPinOpenOrders(isTOSAccepted);
            }}
          />
        )}
        <MarketFooter />
      </div>

      {quickBuySecondaryOptions?.portfolioTOSModal && (
        <ListingAgreement
          isOpen={quickBuySecondaryOptions?.portfolioTOSModal}
          onClose={quickBuySecondaryOptions?.onPortfolioTOSClose}
          onAgreementAccept={quickBuySecondaryOptions?.onPortfolioTOSSuccess}
        />
      )}
    </>
  );
};

export default Portfolio;
